
// Copyright (c) Docugami, Inc. All rights reserved.

import { dgFontFamily } from "components/common/Fonts";
import { ActionsSection } from "components/homepage/ActionsSection";
import { MyWorkSection } from "components/homepage/WorkSection";
import { homeGray, landingPageBackground } from "components/shell/presentation/AppStyles";
import { getFilterTypesForWorkPathCategory, WorkPathCategory } from "components/work_path/WorkPathsHelper";
import { useComponentUsageMetrics } from "hooks/ComponentUsageMetrics";
import { useFeatureFlag } from "hooks/FeatureFlags";
import { useProjectsCoachMark } from "hooks/ProjectsCoachmark";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { FeatureFlags } from "store/featureFlags/types";
import { completeFirstArrivalThunk, selectFirstArrivalState } from "store/organization/organizationSlice";
import styled from "styled-components";
import { getPageViewForWorkPathCategory } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { AutomationId } from "utils/Automation";
import { FirstArrival } from "./FirstArrival";
import { WorkPathType } from "networking/WorkPathApi";

const LandingPageContainer = styled.div`
    max-height: 100vh;
    width: 100%;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    box-sizing: border-box;
    background: ${landingPageBackground};
`;

const LandingPageTitleText = styled.span`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: 500;
    font-size: 36px;
    line-height: 36px;
    display: flex;
    align-items: center;
    text-align: left;
    color: ${homeGray};
    white-space: pre-line;
    gap: 15px;
`;

const LandingPageSubHeading = styled(LandingPageTitleText)`
    font-weight: 400;
    font-size: 18px;
    line-height: 20px;
    flex: 1;
`;

const LandingPageTitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 15px;
    padding: 10px 0;
    box-sizing: border-box;
    align-items: baseline;
`;

export const HomePage = (): JSX.Element => {
    const showFirstArrival = useAppSelector(selectFirstArrivalState);

    const dispatch = useAppDispatch();

    if (showFirstArrival === undefined) {
        return <></>
    }

    return showFirstArrival ?
        <FirstArrival onComplete={() => dispatch(completeFirstArrivalThunk())} /> : <LandingPage />
}

export const LandingPage = (): JSX.Element => {
    const location = useLocation();
    const [filterCategory, setFilterCategory] = useState<WorkPathCategory | undefined>(getCategoryForLocation(location.pathname));
    const demoFeaturesEnabled = useFeatureFlag(FeatureFlags.DemoFeatures);
    const filterTypes = useMemo(() => getFilterTypesForWorkPathCategory(filterCategory, demoFeaturesEnabled), [filterCategory, demoFeaturesEnabled]);
    // The home landing page just shows the create tabular report action
    const actionTypes = useMemo(() => filterCategory ? filterTypes : [WorkPathType.TabularReport], [filterCategory, filterTypes]);

    const coachMark = useProjectsCoachMark(filterCategory);

    useComponentUsageMetrics(filterCategory ?? 'Home');

    useEffect(() => {
        setFilterCategory(getCategoryForLocation(location.pathname));
    }, [location.pathname])

    useEffect(() => {
        Telemetry.trackPageView(getPageViewForWorkPathCategory(filterCategory));
    }, [filterCategory]);

    const categoryToHeadingText = {
        [WorkPathCategory.Understand]: { heading: 'Reports', workTitle: "My Reports", subheading: 'Extract data from your documents to generate reports and gain insights' },
        [WorkPathCategory.Create]: { heading: 'Authoring', workTitle: "Authored Docs", subheading: 'Generate new docs using data from your doc sets' },
        [WorkPathCategory.Workflow]: { heading: 'Automated Workflows', workTitle: "My Workflows", subheading: 'Generate automated workflows utilizing a number of different tools you are already familiar with' },
    };

    return (
        <LandingPageContainer>
            <LandingPageTitle
                title={filterCategory ? categoryToHeadingText[filterCategory].heading.toLocaleUpperCase() : 'Welcome!'}
                subheading={filterCategory ? categoryToHeadingText[filterCategory].subheading : ''} />
            <ActionsSection types={actionTypes} />
            <MyWorkSection title={filterCategory ? categoryToHeadingText[filterCategory].workTitle : 'My Work'} types={filterTypes} coachMark={coachMark} automationId={AutomationId.HomeMyWorkPanel} />
        </LandingPageContainer>
    )
}

interface LandingPageTitleProps {
    title: string;
    subheading: string;
}

export const LandingPageTitle = (props: LandingPageTitleProps): JSX.Element => {
    return (
        <LandingPageTitleContainer>
            <LandingPageTitleText>
                {props.title}
            </LandingPageTitleText>
            <LandingPageSubHeading>{props.subheading}</LandingPageSubHeading>
        </LandingPageTitleContainer>
    )
}

// Returns the WorkPathCategory corresponding to the given pathname
function getCategoryForLocation(pathname: string) {
    switch (pathname) {
        case '/understand':
            return WorkPathCategory.Understand;
        case '/create':
            return WorkPathCategory.Create;
        case '/workflow':
            return WorkPathCategory.Workflow;
        case '/':
        case '/home':
        case '/home/':
        default:
            return;
    }
}
